import PropTypes from 'prop-types';
import {
  Card,
  CardContent,
  CardMedia,
  Box,
  Typography,
  Chip,
  alpha,
  Radio,
} from '@mui/material';
import {
  TEMPLATE_STATUS_STYLES,
  TEMPLATE_CATEGORY_STYLES,
} from '../../../apps/campaigns/constants';
import whatsappBg from '../../../../assets/images/backgrounds/fondodoodle2.png';

const extractImageUrl = (template) => {
  if (template.url) {
    return template.url;
  }

  if (template.example && template.example.includes('|')) {
    const parts = template.example.split('|');
    if (parts.length >= 3 && (parts[0].startsWith('http') || parts[0].startsWith('https'))) {
      return parts[0];
    }
  }
// TODO: Verificar mas estructuras
  if (template.components && Array.isArray(template.components)) {
    const carouselComponent = template.components.find(c => c.type === 'carousel');
    if (carouselComponent?.cards?.[0]?.components) {
      const headerComponent = carouselComponent.cards[0].components.find(c => c.type === 'header');
      if (headerComponent?.parameters?.[0]) {
        const param = headerComponent.parameters[0];
        if (param.type === 'image' && param.image?.link) {
          return param.image.link;
        }
      }
    }

    const headerComponent = template.components.find(c => c.type === 'header');
    if (headerComponent?.parameters?.[0]) {
      const param = headerComponent.parameters[0];
      if (param.type === 'image' && param.image?.link) {
        return param.image.link;
      }
    }
  }

  return null;
};


export const TemplateCard = ({ template, selected, onSelect }) => {
  const statusStyle = TEMPLATE_STATUS_STYLES[template.status] || {};
  const categoryStyle = TEMPLATE_CATEGORY_STYLES[template.category] || {};
  const imageUrl = extractImageUrl(template);


  const handleClick = () => {
    onSelect(template);
  };

 return (
    <Card
      onClick={handleClick}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        minHeight: 420,
        borderRadius: 4,
        cursor: 'pointer',
        border: selected ? 3 : 1,
        borderColor: selected ? 'primary.main' : 'divider',
        position: 'relative',
        transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.2s ease',
        '&:hover': {
          transform: 'translateY(-5px)',
          boxShadow: (theme) => `0 10px 20px ${alpha(theme.palette.primary.main, 0.2)}`,
          borderColor: selected ? 'primary.main' : 'primary.light',
        },
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: 12,
          right: 12,
          zIndex: 10,
          backgroundColor: 'white',
          borderRadius: '50%',
        }}
      >
        <Radio
          checked={selected}
          onChange={handleClick}
          onClick={(e) => e.stopPropagation()}
          size="small"
          sx={{
            '&.Mui-checked': {
              color: 'primary.main',
            },
          }}
        />
      </Box>

      <CardContent sx={{ flexGrow: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
        <Box display="flex" flexDirection="column" gap={1} mb={2} justifyContent="center" alignItems="center">
          <Chip
            label={statusStyle.label || template.status || 'N/A'}
            sx={{
              width: '80%',
              fontWeight: 600,
              fontSize: '0.75rem',
              ...statusStyle,
            }}
          />

          <Chip
            label={template.name || 'Sin nombre'}
            sx={{
              width: '80%',
              fontWeight: 600,
              fontSize: '0.8rem',
              backgroundColor: '#F5F7FA',
              color: '#37474F',
              height: 'auto',
              minHeight: 32,
              '& .MuiChip-label': {
                whiteSpace: 'normal',
                padding: '6px 12px',
              },
            }}
          />
        </Box>

        <Box
          sx={{
            flexGrow: 1,
            backgroundImage: whatsappBg ? `url(${whatsappBg})` : 'none',
            backgroundColor: whatsappBg ? 'transparent' : '#E5DDD5',
            backgroundSize: 'cover',
            borderRadius: 2,
            p: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: 200,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              borderBottomLeftRadius: 0,
              p: imageUrl ? 0 : 1.5, 
              width: '100%',
              maxWidth: 280,
              boxShadow: '0 2px 5px rgba(0,0,0,0.1)',
              overflow: 'hidden',
            }}
          >
            {imageUrl && (
              <CardMedia
                component="img"
                image={imageUrl}
                alt="template-preview"
                sx={{
                  width: '60%',
                  height: '60%',
                  maxHeight: 150,
                  objectFit: 'cover',
                  mb: template.body ? 0 : 0, 
                }}
              />
            )}

            <Box sx={{ p: imageUrl ? 1.5 : 0 }}>
              <Typography
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  fontSize: '0.85rem',
                  lineHeight: 1.4,
                  color: '#000',
                  maxHeight: imageUrl ? '80px' : '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  display: '-webkit-box',
                  WebkitLineClamp: imageUrl ? 3 : 6,
                  WebkitBoxOrient: 'vertical',
                }}
              >
                {template.body || 'Sin contenido disponible'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box display="flex" justifyContent="center" mt={2}>
          {template.category && (
            <Chip
              label={`${categoryStyle.label || template.category}`}
              sx={{
                fontWeight: 600,
                fontSize: '0.75rem',
                ...categoryStyle,
              }}
            />
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

TemplateCard.propTypes = {
  template: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    body: PropTypes.string,
    status: PropTypes.string,
    category: PropTypes.string,
    url: PropTypes.string,
    example: PropTypes.string,
    components: PropTypes.array,
  }).isRequired,
  selected: PropTypes.bool,
  onSelect: PropTypes.func.isRequired,
};

TemplateCard.defaultProps = {
  selected: false,
};